import type { AppySnapshot } from './model'

export type PlanKey = 'starter' | 'creator' | 'studio'

export interface PlanFeatures {
  key: PlanKey
  label: string
  /** Max characters in a text post / short caption. */
  maxPostChars: number
  /** Max image upload size in bytes. */
  maxImageBytes: number
  /** Max video/short upload size in bytes. */
  maxVideoBytes: number
  /** Max short duration seconds. */
  maxShortSeconds: number
  /** Long-form editing, edit window, etc. */
  longFormPosts: boolean
  hdMedia: boolean
  priorityBuild: boolean
  advancedAnalytics: boolean
  longerCaptionsOnApps: boolean
  shortsEnabled: boolean
  multipleImages: boolean
  monthlyCreditsLabel: string
  priceLabel: string
  features: string[]
}

const MB = 1024 * 1024

export const PLAN_FEATURES: Record<PlanKey, PlanFeatures> = {
  starter: {
    key: 'starter',
    label: 'Free',
    maxPostChars: 100,
    maxImageBytes: 8 * MB,
    maxVideoBytes: 40 * MB,
    maxShortSeconds: 30,
    longFormPosts: false,
    hdMedia: false,
    priorityBuild: false,
    advancedAnalytics: false,
    longerCaptionsOnApps: false,
    shortsEnabled: true,
    multipleImages: false,
    monthlyCreditsLabel: 'Pay-as-you-go credits',
    priceLabel: 'Free',
    features: [
      '100 characters per post',
      'Post text, images, and Shorts',
      'Images up to 8MB',
      'Shorts up to 30 seconds · 40MB',
      'Build apps & games with credits',
      'Pay-as-you-go build credits',
      'Basic For You ranking',
    ],
  },
  creator: {
    key: 'creator',
    label: 'Creator',
    maxPostChars: 300,
    maxImageBytes: 32 * MB,
    maxVideoBytes: 150 * MB,
    maxShortSeconds: 60,
    longFormPosts: true,
    hdMedia: true,
    priorityBuild: false,
    advancedAnalytics: false,
    longerCaptionsOnApps: true,
    shortsEnabled: true,
    multipleImages: true,
    monthlyCreditsLabel: '$18 monthly build credits',
    priceLabel: '$24/mo',
    features: [
      'Everything on Free',
      '300 characters per post',
      'HD images up to 32MB',
      'Shorts up to 60 seconds · 150MB',
      'Multiple images on posts',
      'Low, medium, and high intelligence',
      'Daily Game controls',
      'Version memory and app editing',
      '$18 monthly build credits included',
    ],
  },
  studio: {
    key: 'studio',
    label: 'Pro',
    maxPostChars: 25_000,
    maxImageBytes: 100 * MB,
    maxVideoBytes: 500 * MB,
    maxShortSeconds: 180,
    longFormPosts: true,
    hdMedia: true,
    priorityBuild: true,
    advancedAnalytics: true,
    longerCaptionsOnApps: true,
    shortsEnabled: true,
    multipleImages: true,
    monthlyCreditsLabel: '$24 monthly build credits',
    priceLabel: '$34/mo',
    features: [
      'Everything in Creator',
      '25,000 characters per post',
      'Ultra images up to 100MB',
      'Shorts up to 3 minutes · 500MB',
      'Priority AI builds',
      'Higher generation usage limits',
      'Advanced feed signals & analytics',
      '$24 monthly build credits included',
    ],
  },
}

export const planFromBilling = (plan: AppySnapshot['billing']['plan'] | string | undefined): PlanKey => {
  if (plan === 'studio' || plan === 'pro') return 'studio'
  if (plan === 'creator') return 'creator'
  return 'starter'
}

export const featuresForPlan = (plan: AppySnapshot['billing']['plan'] | string | undefined): PlanFeatures =>
  PLAN_FEATURES[planFromBilling(plan)]

export const formatBytes = (bytes: number): string => {
  if (bytes >= MB) return `${Math.round(bytes / MB)}MB`
  return `${Math.round(bytes / 1024)}KB`
}

/** Compact label for large character limits (e.g. 1M, 25k). */
export const formatCharLimit = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
  if (n >= 10_000) return `${Math.round(n / 1000)}k`
  return n.toLocaleString()
}
